import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";

const SECTIONS = [
  { id: "ueber-mich", label: "Über mich" },
  { id: "galerie", label: "Galerie" },
  { id: "kontakt", label: "Kontakt" },
];

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { count } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goto = (id) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document
          .getElementById(id)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } else {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setOpen(false);
  };

  const goHome = () => {
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setOpen(false);
  };

  return (
    <>
      <nav
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled || location.pathname !== "/"
            ? "bg-[#090909]/85 backdrop-blur-md border-b border-[#D4AF37]/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 md:h-20 flex items-center justify-between">
          <button
            onClick={goHome}
            data-testid="nav-logo"
            className="font-serif italic text-lg md:text-xl text-[#E5E5E5] hover:text-[#D4AF37] transition-colors"
          >
            Die Lichtmalerin
          </button>

          {/* Desktop */}
          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {SECTIONS.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-link-${n.id}`}
                onClick={() => goto(n.id)}
                className="text-[11px] font-sans font-light uppercase tracking-[0.35em] text-[#A1A1AA] hover:text-[#D4AF37] transition-colors relative group"
              >
                {n.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#D4AF37] group-hover:w-full transition-all duration-500" />
              </button>
            ))}
            <Link
              to="/shop"
              data-testid="nav-link-shop"
              onClick={() => setOpen(false)}
              className="text-[11px] font-sans font-light uppercase tracking-[0.35em] text-[#A1A1AA] hover:text-[#D4AF37] transition-colors relative group"
            >
              Shop
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#D4AF37] group-hover:w-full transition-all duration-500" />
            </Link>
            <Link
              to="/cart"
              data-testid="nav-cart"
              className="relative inline-flex items-center text-[#D4AF37] hover:text-[#FDE047] transition-colors"
              aria-label="Warenkorb"
            >
              <ShoppingBag size={18} strokeWidth={1.4} />
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -top-2 -right-3 min-w-[16px] h-4 px-1 rounded-full bg-[#D4AF37] text-[#090909] text-[9px] font-sans font-medium flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </Link>
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden flex items-center gap-4">
            <Link
              to="/cart"
              data-testid="nav-cart-mobile"
              className="relative text-[#D4AF37]"
              aria-label="Warenkorb"
            >
              <ShoppingBag size={20} strokeWidth={1.4} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#D4AF37] text-[#090909] text-[9px] font-sans font-medium flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              className="text-[#D4AF37]"
              data-testid="nav-mobile-toggle"
              aria-label="Menü umschalten"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="fixed top-16 inset-x-0 z-30 md:hidden bg-[#090909]/95 backdrop-blur-md border-b border-[#D4AF37]/15"
          >
            <div className="flex flex-col py-6 px-6 gap-5">
              {SECTIONS.map((n) => (
                <button
                  key={n.id}
                  data-testid={`nav-mobile-link-${n.id}`}
                  onClick={() => goto(n.id)}
                  className="text-left text-sm font-sans font-light uppercase tracking-[0.35em] text-[#E5E5E5] hover:text-[#D4AF37] transition-colors py-2"
                >
                  {n.label}
                </button>
              ))}
              <Link
                to="/shop"
                data-testid="nav-mobile-link-shop"
                onClick={() => setOpen(false)}
                className="text-left text-sm font-sans font-light uppercase tracking-[0.35em] text-[#E5E5E5] hover:text-[#D4AF37] transition-colors py-2"
              >
                Shop
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
